import * as React from 'react';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';
import { WarningTwoTone } from '@ant-design/icons';
import { Modal, Button, Divider } from 'src/design-system/components/atoms';
import FixedHeaderTable from 'src/design-system/components/molecules/FixedHeaderTable/FixedHeaderTable';
import { humanize } from 'src/utils/utils';
import lodash from 'lodash';
import { withTranslation, WithTranslation } from 'react-i18next';
import { useTheme } from 'src/theming/useTheme';

const useStyles = createUseStyles((theme: ThemeType) => ({
  failureModal: {
    '& .ant-modal-body': {
      padding: theme.spacing[1],
    },
  },
  errorMessageContainer: {
    padding: theme.spacing[3],
  },
  dividerContainer: {
    marginLeft: theme.spacing[3],
    marginRight: theme.spacing[3],
  },
  failureCountContainer: {
    padding: theme.spacing[3],
    '& span': {
      color: theme.color.semantic.text.primary,
    },
  },
  warningIcon: {
    fontSize: theme.typography.fontSize.lg,
  },
  downloadRow: {
    padding: theme.spacing[2],
    display: 'flex',
    justifyContent: 'center',
  },
  downloadButton: {
    margin: 'auto',
  },
  downloadLink: {
    color: `${theme.color.semantic.text.inverse} !important`,
    textDecoration: 'none',
    '&:hover': {
      color: `${theme.color.semantic.text.inverse} !important`,
    },
  },
  table: {
    marginLeft: theme.spacing[0],
  },
}));

interface FailureColumn {
  title: string;
  dataIndex: string;
}

interface FailureArrayHandlerProps extends WithTranslation {
  failureArray: any[];
  isVisible: boolean;
  onModalClose: () => void;
  showDownloadButton?: boolean;
  failureArrayErrorMessage?: string;
  errorFileLink?: string;
  width?: number;
  failureColumns?: FailureColumn[];
}

interface FailureArrayHandlerState {
  columnNameForTable: FailureColumn[];
}

class FailureArrayHandlerClass extends React.PureComponent<FailureArrayHandlerProps, FailureArrayHandlerState> {
  defaultColumns: FailureColumn[] = [
    {
      title: 'Consignment Number',
      dataIndex: 'reference_number',
    },
    {
      title: 'Reason',
      dataIndex: 'message',
    },
  ];

  state: FailureArrayHandlerState = {
    columnNameForTable: [],
  };

  componentDidMount() {
    const { failureColumns } = this.props;
    this.setState({
      columnNameForTable: failureColumns ? failureColumns : this.defaultColumns,
    });
  }

  generateColumnForTable = () => {
    const { failureArray, t } = this.props;
    const { columnNameForTable } = this.state;
    const columnData: any = [];
    if (failureArray == null || failureArray.length === 0) {
      return columnData;
    }
    const indexList = columnNameForTable;
    indexList.forEach((item) => {
      const itemToPush: any = {};
      itemToPush['title'] = t(item.title);
      itemToPush['dataIndex'] = item.dataIndex;
      itemToPush['key'] = item.dataIndex;
      itemToPush['width'] = item.dataIndex === 'serial_number' ? 40 : 150;
      if (item.dataIndex === 'serial_number') {
        itemToPush['render'] = (text: any, record: any, index: number) => index + 1;
      } else {
        itemToPush['render'] = (text: any, record: any) => {
          return typeof lodash.get(record, item.dataIndex) !== 'string'
            ? text
            : lodash.get(record, item.dataIndex)
            ? humanize(lodash.get(record, item.dataIndex))
            : null;
        };
      }

      columnData.push(itemToPush);
    });

    return columnData;
  };

  handleModalClose = () => {
    this.props.onModalClose();
  };

  render() {
    const {
      failureArray,
      isVisible,
      showDownloadButton,
      failureArrayErrorMessage = '',
      errorFileLink,
      width,
      t,
    } = this.props;

    return (
      <FailureArrayHandlerInner
        failureArray={failureArray}
        isVisible={isVisible}
        showDownloadButton={showDownloadButton}
        failureArrayErrorMessage={failureArrayErrorMessage}
        errorFileLink={errorFileLink}
        width={width}
        t={t}
        generateColumnForTable={this.generateColumnForTable}
        handleModalClose={this.handleModalClose}
      />
    );
  }
}

interface FailureArrayHandlerInnerProps {
  failureArray: any[];
  isVisible: boolean;
  showDownloadButton?: boolean;
  failureArrayErrorMessage: string;
  errorFileLink?: string;
  width?: number;
  t: (key: string) => string;
  generateColumnForTable: () => any[];
  handleModalClose: () => void;
}

const FailureArrayHandlerInner: React.FC<FailureArrayHandlerInnerProps> = ({
  failureArray,
  isVisible,
  showDownloadButton,
  failureArrayErrorMessage,
  errorFileLink,
  width,
  t,
  generateColumnForTable,
  handleModalClose,
}) => {
  const classes = useStyles();
  const theme = useTheme();

  return (
    <Modal
      title={
        <div>
          <WarningTwoTone twoToneColor={theme.color.semantic.accent.pink.bold} className={classes.warningIcon} />{' '}
          {t('errors')}
        </div>
      }
      className={classes.failureModal}
      visible={isVisible}
      onCancel={handleModalClose}
      destroyOnClose
      maskClosable={false}
      width={width || 800}
      okText="Ok"
      footer={null}
    >
      {failureArrayErrorMessage ? (
        <div className={classes.errorMessageContainer}>
          <span>{failureArrayErrorMessage}</span>
        </div>
      ) : null}
      {failureArrayErrorMessage && Array.isArray(failureArray) && failureArray.length ? (
        <>
          <div className={classes.dividerContainer}>
            <Divider />
          </div>
          <div className={classes.failureCountContainer}>
            <span>
              List of failures. <b>Count: {failureArray.length}</b>
            </span>
          </div>
        </>
      ) : null}
      <FixedHeaderTable
        pagination={{
          simple: true,
          hideOnSinglePage: true,
          position: ['topRight'],
          pageSize: 10,
        }}
        columns={generateColumnForTable()}
        dataSource={failureArray}
        rowKey={(record: any, index: number) => record['row_num'] || `failure-${index}`}
        locale={{ emptyText: t('No data found') }}
        className={classes.table}
        minHeight={400}
        enableTextWrap={true}
      />
      {showDownloadButton ? (
        <div className={classes.downloadRow}>
          <Button type="primary" className={classes.downloadButton}>
            <a
              href={errorFileLink}
              className={classes.downloadLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('download_error')}
            </a>
          </Button>
        </div>
      ) : null}
    </Modal>
  );
};

const FailureArrayHandler = withTranslation('translation')(FailureArrayHandlerClass);
export default FailureArrayHandler;
